import { Button, Card, Col, Form, Input, Typography, Row, Space, message, Select, Spin } from 'antd';
import { ButtonType } from 'antd/lib/button';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { useTranslation } from 'react-i18next';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { useToggle } from 'react-use';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import moment from 'moment';

import { BatchesTable } from '../components/batches-table';
import { translationNamespace } from '../constants/translation-resources';
import { useTableOptions } from '../../shared/hooks/use-data-options';
import { Batch, BeDocumentFilters, DocumentsFilters } from '../models/batch';
import { Orders } from '../../shared/constants/order';
import { Order } from '../../shared/models/order';
import { Feature } from '../../shared/models/features';
import { userSelector } from '../../shared/data/store/selectors/auth-selectors';
import { useFindBatchesQuery, useGetFiltersQuery, useGetReasonsQuery } from '../data/queries/batch-queries';
import { BatchesTableFiltersForm } from '../components/batches-table/filters';
import { DocumentsListView } from './documents-list';
import { convertDocumentsFilters } from '../data/converters/documents-filters-converters';
import { useHandleBatchActionMutation } from '../data/mutations/batch-mutations';
import { DocumentActions, DocumentAction } from '../constants/documents';
import { ConfirmationModal } from '../../shared/components/confirmation-modal';
import { isUserAuthorized } from '../../shared/utils/allowed-routes';
import {
  approveBatchLayoutRequiredRoute,
  approveBothBatchRequiredRoute,
  approveQuantityBatchRequiredRoute,
  rejectBatchRequiredRoute,
  setFixDateRequiredRoute,
} from '../constants/required-routes';
import { useGetUserAllowedRoutesQuery } from '../../core/data/queries/core-queries';
import { useApprovalService } from '../data/services/use-approval-service';
import { displayErrors } from '../../shared/utils/error';
import { languageReasonsRequestMap } from '../constants/translation-resources';
import { SetFixDateModal } from '../components/set-fix-date-modal';
import { approvedBatchAndDocumentStatus, disabledBatchAndDocumentStatus } from '../models/document';
import { usePresetBatchesService } from '../data/services/use-presetbatches-service';
import { runBatchDownloads } from '../utils/download';
import { useLocation } from 'react-router-dom';

const { Search, TextArea } = Input;
const { Text } = Typography;

const defaultOrder: [keyof Batch, Order][] = [['expireTime', Orders.ASC]];

export const BatchesListView: FC = () => {
  const location = useLocation();
  // You can now use location.pathname to get the current path

  // Add this to get the current route key if needed
  // const currentRoute = getCurrentRouteKey(location.pathname, navigationItems);

  const { t, i18n } = useTranslation(translationNamespace);
  const currentLanguage = i18n.language;
  const { page, pageSize, setPage, order, handleTableChange } = useTableOptions<Batch>(defaultOrder, 100);
  const currentUser = useSelector(userSelector);
  const actionMutation = useHandleBatchActionMutation();
  const [searchValue, setSearchValue] = useState<string>();
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>();
  const [showFilters, toggleShowFilters] = useToggle(false);
  const [filter, setFilter] = useState<BeDocumentFilters>(null);
  const [filtersForm] = Form.useForm<DocumentsFilters>();
  const [selectedBatchIds, setSelectedBatchIds] = useState<React.Key[]>([]);
  const [rejectionReason, setRejectionReason] = useState<string>();
  const [actionType, setActionType] = useState<DocumentAction>(null);
  const [textareaText, setTextareaText] = useState<string>();
  const [isQueryEnabled, setIsQueryEnabled] = useState<boolean>(false);
  const [isDownloadingBatches, setIsDownloadingBatches] = useState<boolean>(false);
  const [isViewProcessed] = useState<boolean>(location.pathname.endsWith('processed'));
  const { data: batchesData, isLoading: isBatchesDataLoading } = useFindBatchesQuery({
    processed: isViewProcessed,
    page,
    pageSize,
    sortBy: order[0]?.[0],
    sortDirection: order[0]?.[1],
    search: debouncedSearchValue,
    groupId: currentUser?.groups[0]?.id,
    filter,
    enabled: isQueryEnabled,
  });
  const {
    data: filtersColumns,
    isLoading: areFiltersLoading,
    error: filtersError,
  } = useGetFiltersQuery(isViewProcessed, currentUser?.groups[0]?.id, showFilters);
  const { data: userAllowedRoutes } = useGetUserAllowedRoutesQuery();
  const { downloadBatch } = useApprovalService();
  const isRejectedType = actionType === DocumentActions.REJECTED;
  const { data: reasons, isLoading: areReasonsLoading } = useGetReasonsQuery(
    {
      groupId: currentUser?.groups[0]?.id,
      type: 'batch',
      typeAction: DocumentActions.REJECTED,
      lang: languageReasonsRequestMap[currentLanguage],
    },
    isRejectedType
  );
  const [isFixDateModalOpen, toggleFixDateModalOpen] = useToggle(false);

  const presetService = usePresetBatchesService();
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState<string>();
  const [selectedPresetId, setSelectedPresetId] = useState<number | undefined>(undefined);
  const [loadedPresetFilters, setLoadedPresetFilters] = useState<string | null>(null);
  const [presetToDelete, setPresetToDelete] = useState<{ id: number; name: string } | null>(null);
  // Erweitere den pending State um den Suchwert
  const [pendingPresetFilters, setPendingPresetFilters] = useState<{
    filters: DocumentsFilters | null;
    search: string | undefined;
  } | null>(null);

  // Hilfsfunktion: gruppierte State-Änderungen ohne doppelte Requests
  const runAtomicQueryUpdate = useCallback(
    (applyUpdates: () => void, nextEnabled: boolean) => {
      if (isQueryEnabled) {
        // Query vorübergehend deaktivieren, damit nur ein finaler Request ausgelöst wird
        setIsQueryEnabled(false);
        // Updates durchführen und anschließend wieder aktivieren
        // setTimeout garantiert, dass vor dem Reaktivieren alle setState-Änderungen gerendert wurden
        applyUpdates();
        setTimeout(() => setIsQueryEnabled(nextEnabled), 0);
      } else {
        applyUpdates();
        setIsQueryEnabled(nextEnabled);
      }
    },
    [isQueryEnabled]
  );

  const contentText = isRejectedType ? 'reject.modalContent' : 'approve.modalContent';
  const isAuthorizedToApproveLayout = isUserAuthorized(approveBatchLayoutRequiredRoute, userAllowedRoutes);
  const isAuthorizedToApproveNumber = isUserAuthorized(approveQuantityBatchRequiredRoute, userAllowedRoutes);
  const isAuthorizedToApproveBoth = isUserAuthorized(approveBothBatchRequiredRoute, userAllowedRoutes);
  const isAuthorizedToReject = isUserAuthorized(rejectBatchRequiredRoute, userAllowedRoutes);
  const isAuthorizedToSetFixDate = isUserAuthorized(setFixDateRequiredRoute, userAllowedRoutes);

  const loadPresets = useCallback(async () => {
    try {
      const response = await presetService.findPresets(
        {
          attributes: ['id', 'name', 'query', 'processed'],
          limit: 100,
          offset: 0,
        },
        currentUser?.id
      );
      //console.log(response.data.presets ? response.data.presets : '');
      // Nur Presets laden, die zum aktuellen View passen
      const filteredPresets = response.data.presets?.filter(preset => preset.processed === isViewProcessed) || [];
      //console.log(filteredPresets);
      setPresets(filteredPresets);
    } catch (error) {
      displayErrors(error, t);
    }
  }, [presetService, currentUser?.id, t, isViewProcessed]);

  const applyFilters = useCallback(
    tableFilters => {
      const filters = convertDocumentsFilters(tableFilters, filtersColumns?.filter?.columns);
      const hasSearch = !!(searchValue && searchValue.length > 0);
      const shouldEnable = !!filters || hasSearch;

      // Atomare Aktualisierung, damit nur ein Request ausgelöst wird
      runAtomicQueryUpdate(() => {
        if (page !== 1) {
          setPage(1);
        }
        setFilter(filters);
        setDebouncedSearchValue(shouldEnable && hasSearch ? searchValue : undefined);
      }, shouldEnable);

      if (selectedPresetId !== undefined && loadedPresetFilters !== null) {
        const currentFiltersString = JSON.stringify(filtersForm.getFieldsValue());
        if (currentFiltersString !== loadedPresetFilters) {
          setSelectedPresetId(undefined);
          setLoadedPresetFilters(null);
        }
      }
    },
    [
      filtersColumns?.filter?.columns,
      searchValue,
      runAtomicQueryUpdate,
      page,
      setPage,
      selectedPresetId,
      loadedPresetFilters,
      filtersForm,
    ]
  );

  // Suche: Beim Klick auf die Lupe sofort laden (nur wenn Filter oder Suche vorhanden)
  const applySearch = (value: string) => {
    const hasFilters = !!filter;
    const hasSearch = !!(value && value.length > 0);
    const shouldEnable = hasFilters || hasSearch;

    // Atomare Aktualisierung, damit nur ein Request ausgelöst wird
    runAtomicQueryUpdate(() => {
      if (page !== 1) {
        setPage(1);
      }
      setSearchValue(value);
      setDebouncedSearchValue(shouldEnable && hasSearch ? value : undefined);
    }, shouldEnable);

    // Preset-Auswahl zurücksetzen, wenn Suche sich geändert hat
    if (selectedPresetId !== undefined) {
      const currentPreset = presets.find(p => p.id === selectedPresetId);
      const presetSearch = currentPreset?.query?.search || '';

      if (presetSearch !== value) {
        setSelectedPresetId(undefined);
        setLoadedPresetFilters(null);
      }
    }
  };

  useEffect(() => {
    if (filtersError != null) {
      toggleShowFilters(false);
    }
  }, [filtersError, toggleShowFilters]);

  // Presets beim Start laden
  useEffect(() => {
    if (currentUser?.id) {
      loadPresets();
    }
  }, [currentUser?.id, loadPresets]);

  // useEffect: Wende Filter automatisch an, wenn filtersColumns geladen wurden
  useEffect(() => {
    if (pendingPresetFilters && filtersColumns && showFilters) {
      // Filter-Spalten sind jetzt verfügbar, wende die Filter an
      const { filters, search } = pendingPresetFilters;

      const convertedFilters = filters ? convertDocumentsFilters(filters, filtersColumns?.filter?.columns) : null;
      const hasSearch = !!(search && search.length > 0);
      const shouldEnable = !!convertedFilters || hasSearch;

      // Atomare Aktualisierung, damit nur ein Request ausgelöst wird
      runAtomicQueryUpdate(() => {
        if (page !== 1) {
          setPage(1);
        }
        setFilter(convertedFilters);
        setDebouncedSearchValue(shouldEnable && hasSearch ? search : undefined);
      }, shouldEnable);

      setPendingPresetFilters(null);
    }
  }, [pendingPresetFilters, filtersColumns, showFilters, runAtomicQueryUpdate, page, setPage]);

  const hasAnyDateValue = useCallback((value: unknown, visited = new WeakSet<object>()): boolean => {
    if (value == null) return false;

    // antd v4 DatePicker liefert Moment-Objekte
    if (moment.isMoment(value)) return true;
    if (value instanceof Date) return true;

    if (Array.isArray(value)) {
      return value.some(v => hasAnyDateValue(v, visited));
    }

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;

      // Zirkulare Strukturen vermeiden
      if (visited.has(obj)) return false;
      visited.add(obj);

      return Object.values(obj).some(v => hasAnyDateValue(v, visited));
    }

    return false;
  }, []);

  const saveFilterAsPreset = async () => {
    if (!presetName) {
      message.warning(t('batches.preset.nameRequired'));
      return;
    }

    try {
      const currentFilters = filtersForm.getFieldsValue();
      const hasSearch = !!(searchValue && searchValue.length > 0);

      // Prüfen, ob überhaupt Filter oder Suche vorhanden sind
      const hasFilters = Object.values(currentFilters).some(value => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value != null && value !== '' && value !== undefined;
      });

      if (!hasFilters && !hasSearch) {
        message.warning(t('batches.preset.noFiltersOrSearch'));
        return;
      }

      // Hinweis: Datumswerte werden beim Speichern nicht persistiert
      if (hasAnyDateValue(currentFilters)) {
        message.info(t('batches.preset.dateValuesNotSaved'));
      }

      await presetService.createPreset({
        preset: {
          name: presetName,
          userId: currentUser.id,
          query: {
            attributes: [],
            filters: convertDocumentsFilters(currentFilters, filtersColumns?.filter?.columns),
            search: searchValue,
          },
          processed: isViewProcessed,
          createdBy: currentUser.id,
        },
      });
      message.success(t('batches.preset.saved'));
      loadPresets();
      setPresetName('');
    } catch (error) {
      displayErrors(error, t);
    }
  };

  const loadPreset = async (presetId: number) => {
    try {
      const preset = await presetService.getPreset(presetId);
      setSelectedPresetId(presetId);

      // Filter aufklappen
      if (!showFilters) {
        toggleShowFilters(true);
      }

      // Form-Felder zurücksetzen (UI)
      filtersForm.resetFields();

      // Preset-Filter und -Suche anwenden
      const presetFilters = preset.query.filters as DocumentsFilters | undefined;
      const presetSearch = preset.query.search as string | undefined;

      let normalizedFilters: DocumentsFilters | undefined = undefined;

      if (presetFilters) {
        // Rückkonvertierung: frmEx -> frm + frmExclude
        normalizedFilters = { ...presetFilters };

        // Wenn frmEx vorhanden ist, konvertiere zurück zu frm und frmExclude
        if (normalizedFilters.frmEx !== undefined) {
          normalizedFilters.frm = normalizedFilters.frmEx;
          normalizedFilters.frmExclude = true;
          delete normalizedFilters.frmEx;
        }

        filtersForm.setFieldsValue(normalizedFilters);
      }

      // Setze Suchwert
      setSearchValue(presetSearch);

      // Setze pending filters UND search, damit sie angewendet werden, sobald filtersColumns verfügbar sind
      setPendingPresetFilters({
        filters: normalizedFilters || null,
        search: presetSearch,
      });

      // Geladene Preset-Filter referenzieren (für spätere Änderungen)
      setLoadedPresetFilters(JSON.stringify(normalizedFilters ?? {}));

      message.success(t('batches.preset.loaded'));
    } catch (error) {
      displayErrors(error, t);
    }
  };

  const deletePreset = async (presetId: number) => {
    try {
      await presetService.deletePreset(presetId);
      message.success(t('batches.preset.deleted'));
      loadPresets();
      setPresetToDelete(null);

      // Falls das gelöschte Preset gerade ausgewählt war, Auswahl zurücksetzen
      if (selectedPresetId === presetId) {
        setSelectedPresetId(undefined);
        setLoadedPresetFilters(null);
      }
    } catch (error) {
      displayErrors(error, t);
    }
  };

  const confirmDeletePreset = () => {
    if (presetToDelete) {
      deletePreset(presetToDelete.id);
    }
  };

  const cancelDeletePreset = () => {
    setPresetToDelete(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchValue(newValue);

    // Preset zurücksetzen, sobald sich der Suchwert ändert
    if (selectedPresetId !== undefined) {
      const currentPreset = presets.find(p => p.id === selectedPresetId);
      const presetSearch = currentPreset?.query?.search || '';

      if (presetSearch !== newValue) {
        setSelectedPresetId(undefined);
        setLoadedPresetFilters(null);
      }
    }
  };

  const handleFilterValuesChange = () => {
    // Preset-Auswahl zurücksetzen bei manueller Filteränderung
    if (selectedPresetId !== undefined && loadedPresetFilters !== null) {
      const currentFiltersString = JSON.stringify(filtersForm.getFieldsValue());
      if (currentFiltersString !== loadedPresetFilters) {
        setSelectedPresetId(undefined);
        setLoadedPresetFilters(null);
      }
    }
  };

  // Leeren-Logik: Eingaben zurücksetzen, aber aktuelle Ergebnisliste NICHT beeinflussen
  const handleClearFilters = () => {
    // Form-Felder leeren (UI)
    filtersForm.resetFields();
    // Preset-Auswahl leeren (UI)
    setSelectedPresetId(undefined);
    setLoadedPresetFilters(null);

    // WICHTIG:
    // - debouncedSearchValue NICHT ändern (sonst würde die aktive Suche verloren gehen)
    // - filter NICHT ändern (sonst würden aktive Filter verloren gehen)
    // - isQueryEnabled NICHT ändern (sonst würde die Tabelle ausgeblendet)
    // - page NICHT ändern
  };

  const onFinishAction = () => {
    setSelectedBatchIds([]);
    setActionType(null);
    setRejectionReason(undefined);
    setTextareaText('');
  };

  const onCancel = () => {
    setActionType(null);
    setRejectionReason(undefined);
    setTextareaText('');
  };

  const confirmAction = () => {
    // Doppelklick/Mehrfach-Submit verhindern, während die Mutation läuft
    if (actionMutation.isLoading) return;

    const success = isRejectedType ? 'reject.success' : 'approve.success';

    actionMutation.mutate(
      {
        user: currentUser.username,
        msg: rejectionReason,
        comment: textareaText,
        wIds: filteredBatchIds as string[],
        actionType: actionType,
      },
      {
        onSuccess: () => {
          message.success(
            t(success, {
              resourceType: t('batches.batch'),
            })
          );
        },
        onSettled: () => onFinishAction(),
      }
    );
  };

  function mayRejectOrApprove(batch: Batch) {
    const res =
      !disabledBatchAndDocumentStatus.includes(batch.status) && !approvedBatchAndDocumentStatus.includes(batch.status);
    return res;
  }

  function extractKey(k: string) {
    return k.substring(0, k.indexOf('+'));
  }

  function filteredSelectedBatches() {
    if (!batchesData || !selectedBatchIds) return [];

    const batches = batchesData?.resources?.filter(batch =>
      selectedBatchIds.some(wId => extractKey(wId.toString()) === batch.wId)
    );

    return batches.filter(mayRejectOrApprove);
  }

  function filteredSelectedBatchIds() {
    return filteredSelectedBatches().map(b => b.wId);
  }

  function isSharedButtonDisabled() {
    const res = filteredSelectedBatches().filter(mayRejectOrApprove).length === 0;
    return res;
  }

  const sharedButtonProps = {
    type: 'primary' as ButtonType,
    size: 'large' as SizeType,
    disabled: isSharedButtonDisabled(),
    style: { marginTop: '10px', marginLeft: '20px' },
  };

  const download = async () => {
    setIsDownloadingBatches(true);

    try {
      const resourcesToDownload =
        batchesData?.resources?.filter(resource =>
          selectedBatchIds.some(wId => extractKey(wId.toString()) === resource.wId)
        ) ?? [];

      if (resourcesToDownload.length === 0) return;

      const { ok, failedDetails } = await runBatchDownloads({
        resources: resourcesToDownload.map(r => ({ fileName: r.fileName, pdfLink: r.pdfLink })),
        downloadBatch,
        t,
        retry: { retries: 2, baseDelayMs: 350, perAttemptDelayMs: 150 },
      });

      const total = resourcesToDownload.length;

      const renderFailedDetails = () => {
        const maxToShow = 5;
        const items = failedDetails.slice(0, maxToShow);
        const remaining = failedDetails.length - items.length;

        return (
          <div style={{ textAlign: 'left' }}>
            <div style={{ display: 'grid', rowGap: 2, marginTop: 6 }}>
              {items.map((f, idx) => (
                <div key={`${f.fileName}-${idx}`} style={{ whiteSpace: 'pre-wrap' }}>
                  {f.fileName}
                  {f.reason ? ` (${f.reason})` : ''}
                </div>
              ))}
              {remaining > 0 && (
                <div style={{ marginTop: 4 }}>{t('batches.download.moreErrors', { count: remaining })}</div>
              )}
            </div>
          </div>
        );
      };

      if (failedDetails.length === 0) {
        message.success(<div>{t('batches.download.successSummary', { ok: ok.length, total })}</div>);
      } else {
        message.error({
          duration: 6,
          content: (
            <div>
              <div>{t('batches.download.failedSummary', { failed: failedDetails.length, total })}</div>
              {renderFailedDetails()}
            </div>
          ),
        });
      }
    } catch (error) {
      displayErrors(error, t);
    } finally {
      setIsDownloadingBatches(false);
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedBatchIds,
    onChange: setSelectedBatchIds,
    getCheckboxProps: (batch: Batch) => ({
      disabled: false,
    }),
  };

  const applyStyles = (batch: Batch) => {
    return disabledBatchAndDocumentStatus.includes(batch.status)
      ? { style: { opacity: 0.5, backgroundColor: '#7F7F7F' } }
      : approvedBatchAndDocumentStatus.includes(batch.status)
      ? { style: { opacity: 0.5, backgroundColor: '#6CA885' } }
      : {};
  };

  const renderRowClassName = row => {
    if (row.status === 'approved' || row.status === 'rejected') {
      return 'disabled-row';
    }
    return '';
  };

  const expandedRowRender = (batch: Batch) => {
    return (
      <DocumentsListView
        batchName={batch.fileName}
        filter={filter}
        search={debouncedSearchValue}
        wId={batch.wId}
        processed={isViewProcessed}
      />
    );
  };

  const filteredBatchIds = filteredSelectedBatchIds();

  const fixDateBatches = useMemo(() => {
    if (batchesData == null) return [];

    return batchesData?.resources.reduce((acc, batch) => {
      if (filteredBatchIds.includes(batch.wId)) {
        acc.push(batch);
      }
      return acc;
    }, [] as Batch[]);
  }, [batchesData, filteredBatchIds]);

  return (
    <Row justify="space-between" gutter={[16, 24]}>
      <Col style={{ width: '100%' }}>
        <span style={{ margin: 'auto' }}>{t(`filterInfo`)}</span>
      </Col>
      <Col style={{ width: '100%' }}>
        <Space>
          {/* Preset-Dropdown */}
          <Select
            placeholder={t('batches.preset.load')}
            style={{ width: 320 }}
            size="large"
            //allowClear
            value={selectedPresetId}
            onChange={loadPreset}
            optionLabelProp="label"
          >
            {presets.map(preset => (
              <Select.Option key={preset.id} value={preset.id} label={preset.name}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <span>{preset.name}</span>
                  <Button
                    type="text"
                    size="small"
                    danger
                    style={{ marginLeft: 'auto' }}
                    onClick={e => {
                      e.stopPropagation();
                      setPresetToDelete({ id: preset.id, name: preset.name });
                    }}
                  >
                    {t('batches.preset.delete')}
                  </Button>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Space>
      </Col>
      <Col style={{ width: '100%' }}>
        <Space>
          <StyledSearch
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={t(`${Feature.SHARED}:search`)}
            onSearch={applySearch}
            allowClear
            size="large"
          />
          <Button type="link" onClick={toggleShowFilters}>
            {t(`${Feature.SHARED}:filters`)} <StyledDownOutlined rotate={showFilters ? 180 : 0} />
          </Button>
        </Space>
      </Col>
      {showFilters && filtersError == null && (
        <Col span={24}>
          <Card title={t(`${Feature.SHARED}:filters`)}>
            {!filtersColumns || areFiltersLoading ? (
              <Spin />
            ) : (
              <Row>
                <BatchesTableFiltersForm
                  form={filtersForm}
                  onApply={applyFilters}
                  filtersColumns={filtersColumns}
                  areFiltersLoading={areFiltersLoading}
                  onValuesChange={handleFilterValuesChange}
                  processed={isViewProcessed}
                />
              </Row>
            )}
          </Card>
        </Col>
      )}

      {showFilters && filtersError == null && (
        <Col span={24}>
          <Row gutter={16} justify="space-between" align="middle" style={{ marginTop: 12 }}>
            <Col>
              <Row gutter={16}>
                <Col>
                  <Button type="primary" size="large" onClick={() => filtersForm.submit()}>
                    {t(`${Feature.SHARED}:apply`)}
                  </Button>
                </Col>
                <Col>
                  <Button size="large" onClick={handleClearFilters}>
                    {t(`${Feature.SHARED}:clear`)}
                  </Button>
                </Col>
              </Row>
            </Col>
            <Col>
              <Row gutter={16}>
                <Col>
                  <Input
                    placeholder={t('batches.preset.enterName')}
                    value={presetName}
                    size="large"
                    style={{ width: 320 }}
                    onChange={e => setPresetName(e.target.value)}
                    onPressEnter={saveFilterAsPreset}
                  />
                </Col>
                <Col>
                  <Button type="default" size="large" onClick={saveFilterAsPreset}>
                    {t('batches.preset.save')}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      )}

      <Col span={24}>
        <Row style={{ justifyContent: 'end' }}>
          {isAuthorizedToReject && !isViewProcessed && (
            <Button {...sharedButtonProps} css={stornoButton} onClick={() => setActionType(DocumentActions.REJECTED)}>
              {t('reject.button')}
            </Button>
          )}
          {(isAuthorizedToApproveLayout || isAuthorizedToApproveBoth) && !isViewProcessed && (
            <Button
              css={marginButton}
              onClick={() => setActionType(DocumentActions.APPROVED_LAYOUT)}
              {...sharedButtonProps}
            >
              {t('approve.layout')}
            </Button>
          )}
          {(isAuthorizedToApproveNumber || isAuthorizedToApproveBoth) && !isViewProcessed && (
            <Button
              css={marginButton}
              onClick={() => setActionType(DocumentActions.APPROVED_QUANTITY)}
              {...sharedButtonProps}
            >
              {t('approve.quantity')}
            </Button>
          )}
          {isAuthorizedToApproveBoth && !isViewProcessed && (
            <Button
              css={marginButton}
              onClick={() => setActionType(DocumentActions.APPROVED_BOTH)}
              {...sharedButtonProps}
            >
              {t('approve.both')}
            </Button>
          )}
          <Button
            {...sharedButtonProps}
            disabled={!batchesData?.resources || batchesData?.resources.length === 0}
            onClick={download}
            loading={isDownloadingBatches}
          >
            {t(`${Feature.SHARED}:download`)}
          </Button>
          {isAuthorizedToSetFixDate && !isViewProcessed && (
            <Button {...sharedButtonProps} disabled={isSharedButtonDisabled()} onClick={toggleFixDateModalOpen}>
              {t('batches.fixDate.set')}
            </Button>
          )}
        </Row>
      </Col>
      <Col span={24}>
        {isQueryEnabled ? (
          <StyledTable
            dataSource={batchesData?.resources}
            loading={isBatchesDataLoading}
            rowSelection={rowSelection}
            scroll={{ x: 600 }}
            pagination={{
              total: batchesData?.total,
              current: page,
              pageSize,
              showSizeChanger: batchesData?.total > 10,
              pageSizeOptions: ['50', '100', '500', '1000'],
              position: ['topRight', 'bottomRight'],
            }}
            onRow={batch => applyStyles(batch)}
            rowClassName={renderRowClassName}
            onChange={handleTableChange}
            defaultOrder={defaultOrder[0]}
            processed={isViewProcessed}
            title={() => (
              <Text strong>
                {t(`${Feature.APPROVAL}:documentsTable.totalDocuments`)} {batchesData?.numberOfDocuments}
              </Text>
            )}
            expandable={{ expandedRowRender }}
          />
        ) : (
          <span style={{ margin: 'auto' }}>{t(`noSelection`)}</span>
        )}
      </Col>

      <ConfirmationModal
        destroyOnClose
        open={presetToDelete != null}
        title={t(`${Feature.SHARED}:areYouSure`)}
        content={t('batches.preset.deleteConfirmation', {
          presetName: presetToDelete?.name || '',
        })}
        onCancel={cancelDeletePreset}
        onOk={confirmDeletePreset}
      />

      <SetFixDateModal
        toggleModal={toggleFixDateModalOpen}
        batches={fixDateBatches}
        open={isFixDateModalOpen}
        onCancel={toggleFixDateModalOpen}
      />

      <ConfirmationModal
        destroyOnClose
        open={actionType != null}
        title={t(`${Feature.SHARED}:areYouSure`)}
        content={t(contentText, {
          buttonTitle: t(`${Feature.SHARED}:confirm`),
          amount: `${filteredSelectedBatches().length}`,
          resourceType: t('batches.batch'),
        })}
        onCancel={onCancel}
        onOk={confirmAction}
        okButtonProps={{
          disabled: (isRejectedType && rejectionReason == null) || actionMutation.isLoading,
          loading: actionMutation.isLoading,
        }}
      >
        {isRejectedType && (
          <>
            <p style={{ marginTop: '10px', width: '100%', color: '#d32f2f' }}>
              <strong>{t('reject.warningPrefix')}</strong> {t('reject.warningRest')}
            </p>
            <p style={{ marginTop: '10px', width: '100%' }}>{t('reject.reason')}</p>
            <Select
              style={{ width: '100%' }}
              options={reasons}
              onSelect={setRejectionReason}
              loading={areReasonsLoading}
            />
            <TextArea
              style={{ marginTop: '10px' }}
              rows={4}
              placeholder={t('reject.comment')}
              onChange={event => setTextareaText(event.target.value)}
            />
          </>
        )}
      </ConfirmationModal>
    </Row>
  );
};

const StyledSearch = styled(Search)`
  max-width: 320px;
`;

const StyledDownOutlined = styled(DownOutlined)`
  > svg {
    transition: transform 0.2s;
  }
`;

export const marginButton = css`
  margin-left: 20px;
`;

export const stornoButton = css`
  background-color: #d45b00;
`;

const StyledTable = styled(BatchesTable)`
  @media (min-width: 2051px) {
    max-width: calc(100vw - 250px);
  }
`;
